/**
 * @file index.tsx
 *
 * @fileoverview Renders the consent popup.
 */
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import QUERY_GET_LATEST_CONSENT from '~/jobiqo/components/GDPRConsent/graphql/queries/haveLatestConsent.gql';
import CREATE_GDPR_USER_CONSENT from '~/jobiqo/components/GDPRConsent/graphql/mutations/createGdprUserConsent.gql';
import GET_GDPR_CONSENT_INFOS from '~/jobiqo/components/GDPRConsent/graphql/queries/getGdprConsentInfos.gql';
import React, { useContext, useState } from 'react';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { GdprConsentPlacement, GdprConsentType } from '~/jobiqo/types/types';
import { useFlags } from '~/jobiqo/hooks/flags';
import { Controller, useForm } from 'react-hook-form';
import { Modal } from '~/vcl/atoms/15-modal';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { CenterForm } from '~/vcl/organisms/01-form/01-center';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Title } from '@radix-ui/react-alert-dialog';
import FlexItem from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import * as Yup from 'yup';
import { Checkbox } from '~/vcl/atoms/01-forms/02-checkbox';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import RichFormattedMessage from '~/jobiqo/components/RichFormattedMessage';
import { PrivacyPolicyLink } from '~/jobiqo/components/PrivacyPolicyLink';

export interface GdprConsentModalProps {
  /**
   * The gdpr placement.
   */
  placement: GdprConsentPlacement;
  /**
   * The gdpr type.
   */
  type: GdprConsentType;
}
interface FormValues {
  consent: string;
  newsletter?: string;
}

export const GdprConsentModal = ({
  placement,
  type,
}: GdprConsentModalProps) => {
  const intl = useIntl();
  const { APP } = useFrontendConfig();
  const flagService = useFlags();
  const { user }: UserContextInterface = useContext(UserContext);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(true);

  // Ensure that the user has the latest consent.
  const {
    data: latestConsentData,
    error: latestConsentError,
    loading: latestConsentLoading,
  } = useQuery(QUERY_GET_LATEST_CONSENT, {
    variables: {
      type: type,
      placement: placement,
    },
  });

  const {
    data: availableConsents,
    error: availableConsentError,
    loading: availableConsentsLoading,
  } = useQuery(GET_GDPR_CONSENT_INFOS, {
    variables: {
      placement: placement,
      type: type,
    },
  });

  // Ensure that the user has the latest newsletter consent.
  const { data: latestNewsletterData, error: latestNewsletterError } = useQuery(
    QUERY_GET_LATEST_CONSENT,
    {
      variables: {
        type: 'NEWSLETTER',
        placement: placement,
      },
      skip:
        !APP.NEWSLETTER_FEATURE_ACTIVE ||
        !APP.GDPR_CONSENT_MODAL_SHOW_NEWSLETTER,
    },
  );

  const {
    data: availableNewsletters,
    error: availableNewslettersError,
  } = useQuery(GET_GDPR_CONSENT_INFOS, {
    variables: {
      placement: placement,
      type: 'NEWSLETTER',
    },
    skip:
      !APP.NEWSLETTER_FEATURE_ACTIVE || !APP.GDPR_CONSENT_MODAL_SHOW_NEWSLETTER,
  });

  const [createGdprUserConsent] = useMutation(CREATE_GDPR_USER_CONSENT);

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(
      Yup.object().shape({
        consent: Yup.boolean()
          .required(
            intl.formatMessage({
              id: 'gdpr_consent_modal_terms_accept_error',
              defaultMessage: 'Please accept the terms and conditions',
            }),
          )
          .oneOf(
            [true],
            intl.formatMessage({
              id: 'gdpr_consent_modal_terms_accept_error',
              defaultMessage: 'Please accept the terms and conditions',
            }),
          ),
      }),
    ),
  });

  if (
    latestConsentError ||
    availableConsentError ||
    (APP.GDPR_CONSENT_MODAL_SHOW_NEWSLETTER && latestNewsletterError) ||
    (APP.GDPR_CONSENT_MODAL_SHOW_NEWSLETTER && availableNewslettersError)
  ) {
    flagService.addFlag({
      type: 'danger',
      text: intl.formatMessage({
        id: 'gdpr_consent_modal_error_loading',
        defaultMessage: `There was an error loading the GDPR consent.`,
      }),
    });

    return <div></div>;
  }

  // Display only if the user doesn't have latest consent.
  if (
    // Nothing is loaded
    !latestConsentLoading &&
    !availableConsentsLoading &&
    !latestConsentData?.hasTheLatestConsent &&
    availableConsents &&
    availableConsents.getGdprConsentInfos
  ) {
    // Since we retrieve the array from the backend, we select first one.
    let availableConsent = availableConsents.getGdprConsentInfos[0];
    let availableNewsletter = availableNewsletters?.getGdprConsentInfos[0];
    return (
      <Modal
        isShown={showRemoveConfirm}
        id="gdpr_consent_modal"
        maxWidth="550px"
        showClose={APP.GDPR_CONSENT_MODAL_SHOW_CLOSE}
        // If close button is disable we want to disable also closing modal with ESC key.
        disableEscapeKeyClose={!APP.GDPR_CONSENT_MODAL_SHOW_CLOSE}
        onHide={() => setShowRemoveConfirm(false)}
      >
        <Title>
          <FormattedMessage
            id="gdpr_consent_modal_title"
            defaultMessage="Our terms and conditions have changed"
            values={{ br: <br /> }}
          />
        </Title>
        <Spacing />
        <RichFormattedMessage
          id="gdpr_consent_modal_text_body"
          defaultMessage="To continue, please review and accept our terms and conditions. They reflect our dedication to safeguarding your privacy, strengthening security, and ensuring transparency in how we use data to enhance your experience.
                      {br}{br} Read our {privacy_link}."
          values={{
            br: <br />,
            privacy_link: <PrivacyPolicyLink inline />,
          }}
        />
        <Spacing />
        <Spacing size="medium" />
        <CenterForm
          minWidth="250px"
          onSubmit={handleSubmit((formData) => {
            // Send only if the checkbox was checked.
            if (formData?.consent) {
              createGdprUserConsent({
                variables: {
                  mail: user?.email,
                  infoId: availableConsent.id,
                },
              }).then(() => {
                if (availableNewsletter && formData?.newsletter) {
                  createGdprUserConsent({
                    variables: {
                      mail: user?.email,
                      infoId: availableNewsletter.id,
                    },
                  });
                }
                setShowRemoveConfirm(false);
                flagService.addFlag({
                  type: 'success',
                  text: intl.formatMessage({
                    id: 'gdpr_consent_modal_success',
                    defaultMessage: `Your consent to the latest GDPR changes has been saved.`,
                  }),
                });
              });
            }
            setShowRemoveConfirm(false);
          })}
          method="POST"
        >
          <Controller
            name="consent"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Checkbox
                  {...field}
                  id={field.name}
                  type="checkbox"
                  label={
                    <FormattedMessage
                      id="gdpr_consent_modal_checkbox_label"
                      defaultMessage="I agree with the terms and conditions."
                    />
                  }
                  required={true}
                  data-test-id="gdpr-consent-term-checkbox"
                />
                {error ? (
                  <>
                    <Spacing />
                    <InlineError message={error.message} />
                  </>
                ) : null}
              </>
            )}
          />
          <Spacing />
          {APP.NEWSLETTER_FEATURE_ACTIVE &&
            APP.GDPR_CONSENT_MODAL_SHOW_NEWSLETTER &&
            !latestNewsletterData?.hasTheLatestConsent && (
              // Render newsletter checkbox only when feature is enabled and the user didn't subscribe
              // to the newsletter yet.
              <>
                <Controller
                  name="newsletter"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Checkbox
                      {...field}
                      id="newsletter"
                      type="checkbox"
                      checked={latestNewsletterData?.hasTheLatestConsent}
                      label={
                        <FormattedMessage
                          id="gdpr_consent_modal_newsletter_checkbox_label"
                          defaultMessage="I want to receive the newsletter."
                        />
                      }
                      data-test-id="gdpr-consent-newsletter-checkbox"
                    />
                  )}
                />
                <Spacing />
              </>
            )}
          <FlexItems
            flexDirection={['column', 'row']}
            alignItems="center"
            flexWrap="wrap"
            justifyContent="center"
            gap={[4, 6]}
          >
            <FlexItem>
              <Button
                disabled={!isValid}
                buttonStyle="primary"
                raised={true}
                onClick={() => {
                  setShowRemoveConfirm(false);
                }}
                data-testid="gdpr-consent-modal-confirm-button"
              >
                <FormattedMessage
                  id="gdpr_consent_modal_confirm_button"
                  defaultMessage="Confirm"
                />
              </Button>
            </FlexItem>
          </FlexItems>
        </CenterForm>
      </Modal>
    );
  } else {
    return <div></div>;
  }
};
export default GdprConsentModal;
